"use client";
import { useEffect, useRef, useState } from "react";
import "./AssistantWidget.css";

interface Msg { role: "user" | "assistant"; content: string }

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const convRef = useRef<string | null>(null);
  const orderRef = useRef<{ o: string; t: string } | null>(null);
  const introDone = useRef(false);

  const lang = typeof navigator !== "undefined" && (navigator.language || "").toLowerCase().startsWith("pl") ? "pl" : "en";

  useEffect(() => {
    convRef.current = localStorage.getItem("fablio_conv");
    const params = new URLSearchParams(window.location.search);
    const o = params.get("o");
    const t = params.get("t");
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (o && t) {
      orderRef.current = { o, t };
      // odroczone poza synchroniczny bieg efektu (unika kaskadowych renderów): otwórz + proaktywne intro
      if (!introDone.current) {
        introDone.current = true;
        timer = setTimeout(() => { setOpen(true); void stream({ intro: true }); }, 0);
      }
    }
    return () => { if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function stream(payload: Record<string, unknown>) {
    setBusy(true);
    setMsgs((m) => [...m, { role: "assistant", content: "" }]);
    const ord = orderRef.current;
    try {
      const res = await fetch("/api/assistant", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lang, conversationId: convRef.current ?? undefined, orderId: ord?.o, orderToken: ord?.t, ...payload }),
      });
      if (!res.body) throw new Error("no body");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop() ?? "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]" || !data) continue;
          try {
            const j = JSON.parse(data);
            if (j.conversationId) { convRef.current = j.conversationId; localStorage.setItem("fablio_conv", j.conversationId); }
            if (j.delta) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + j.delta }; return c; });
            if (j.error) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: lang === "pl" ? "Przepraszam, coś poszło nie tak. Napisz na hello@fablio.me." : "Sorry, something went wrong. Email hello@fablio.me." }; return c; });
          } catch { /* ignore */ }
        }
      }
    } catch {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: lang === "pl" ? "Błąd połączenia." : "Connection error." }; return c; });
    } finally { setBusy(false); }
  }

  function send() {
    const message = input.trim();
    if (!message || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: message }]);
    void stream({ message });
  }

  // Link logowania pokazujemy tylko w trybie zamówienia (wejście z maila) — panel renderuje się
  // dopiero po kliencie, więc odczyt window jest bezpieczny (brak wersji SSR do rozjazdu).
  const loginHref = orderRef.current && typeof window !== "undefined"
    ? `/login?callbackUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`
    : null;

  return (
    <div className="aw-root">
      {open && (
        <div className="aw-panel">
          <div className="aw-head">{lang === "pl" ? "Pomoc Fablio" : "Fablio help"}<button className="aw-x" onClick={() => setOpen(false)}>×</button></div>
          <div className="aw-body">
            {msgs.length === 0 && <p className="aw-hint">{lang === "pl" ? "Zadaj pytanie o Fablio." : "Ask anything about Fablio."}</p>}
            {msgs.map((m, i) => <div key={i} className={`aw-msg aw-${m.role}`}>{m.content || "…"}</div>)}
            {loginHref && <a className="aw-login" href={loginHref}>{lang === "pl" ? "Zaloguj się, aby zobaczyć wszystkie bajki →" : "Log in to see all your stories →"}</a>}
          </div>
          <form className="aw-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input className="aw-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={lang === "pl" ? "Napisz…" : "Type…"} />
            <button className="aw-send" disabled={busy}>→</button>
          </form>
        </div>
      )}
      <button className="aw-bubble" onClick={() => setOpen((o) => !o)} aria-label="Chat">💬</button>
    </div>
  );
}
